import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Notification, NotificationService } from '../../core/services';

interface ToastItem extends Notification {
  id: number;
  remainingMs: number;
  startedAt: number;
  timeoutId?: ReturnType<typeof setTimeout>;
  /** Whole seconds left, shown as "4s" etc. derived from remainingMs/
   *  startedAt each tick, not decremented on its own, so it can never
   *  drift out of sync with the real dismiss timer those two drive. */
  remainingSeconds: number;
  tickIntervalId?: ReturnType<typeof setInterval>;
}

const AUTO_DISMISS_MS = 4000;
// Ticks faster than 1s so the displayed number flips right when each
// second actually elapses, not up to a second late.
const TICK_MS = 250;

@Component({
  selector: 'app-notification-toast',
  standalone: false,
  templateUrl: './notification-toast.html',
  styleUrl: './notification-toast.scss',
})
export class NotificationToast implements OnInit, OnDestroy {
  private readonly _destroy$ = new Subject<void>();
  private nextId = 0;

  toasts: ToastItem[] = [];

  constructor(private notificationService: NotificationService) {}

  ngOnInit(): void {
    this.notificationService.notifications$
      .pipe(takeUntil(this._destroy$))
      .subscribe(notification => this.show(notification));
  }

  private show(notification: Notification): void {
    const toast: ToastItem = {
      ...notification,
      id: this.nextId++,
      remainingMs: AUTO_DISMISS_MS,
      startedAt: Date.now(),
      remainingSeconds: Math.ceil(AUTO_DISMISS_MS / 1000),
    };
    this.toasts.push(toast);
    this.scheduleDismiss(toast);
    this.scheduleTick(toast);
  }

  private scheduleDismiss(toast: ToastItem): void {
    toast.startedAt = Date.now();
    toast.timeoutId = setTimeout(() => this.dismiss(toast.id), toast.remainingMs);
  }

  private scheduleTick(toast: ToastItem): void {
    toast.tickIntervalId = setInterval(() => {
      const remaining = toast.startedAt + toast.remainingMs - Date.now();
      toast.remainingSeconds = Math.max(0, Math.ceil(remaining / 1000));
    }, TICK_MS);
  }

  pauseTimer(toast: ToastItem): void {
    if (toast.timeoutId == null) return;
    clearTimeout(toast.timeoutId);
    toast.timeoutId = undefined;
    toast.remainingMs -= Date.now() - toast.startedAt;
    if (toast.tickIntervalId != null) {
      clearInterval(toast.tickIntervalId);
      toast.tickIntervalId = undefined;
    }
  }

  resumeTimer(toast: ToastItem): void {
    if (toast.timeoutId != null || toast.remainingMs <= 0) return;
    this.scheduleDismiss(toast);
    this.scheduleTick(toast);
  }

  dismiss(id: number): void {
    const toast = this.toasts.find(t => t.id === id);
    if (toast?.timeoutId != null) clearTimeout(toast.timeoutId);
    if (toast?.tickIntervalId != null) clearInterval(toast.tickIntervalId);
    this.toasts = this.toasts.filter(t => t.id !== id);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this.toasts.forEach(t => {
      if (t.timeoutId != null) clearTimeout(t.timeoutId);
      if (t.tickIntervalId != null) clearInterval(t.tickIntervalId);
    });
  }
}
